import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";

import { getProjectRepository } from "../../data/projectRepository";
import type { ResourceRoomProjectDocumentV2, Student } from "../../storage/resourceRoomDocument";
import { CourseNaturePage } from "../course-natures/CourseNaturePage";
import { ResourceRoomDialog } from "../shared/ResourceRoomDialog";
import { ResourceRoomProjectHeader } from "../shared/ResourceRoomProjectHeader";
import {
  buildDemoStudentTimetableWorkbook,
  demoStudentProfiles
} from "../shared/resourceRoomDemoData";
import "../shared/resource-room-workspace.css";
import {
  downloadStudentTimetableTemplate,
  parseStudentTimetableFile,
  periodOptions,
  type StudentTimetableWorkbook
} from "./studentTimetableWorkbook";
import { SupplementaryTimeSlotEditor } from "./SupplementaryTimeSlotEditor";

export type SchoolDataTask = "ORIGINAL" | "STUDENTS" | "TIME_SLOTS" | "COURSE_NATURES";

interface ImportPreview {
  valid: boolean;
  student_count: number;
  course_count: number;
  time_slot_count: number;
  retained_student_count: number;
  errors: Array<{ path: string; message: string; type: string }>;
  students: Array<{ import_key: string; action: "ADD" | "REPLACE" }>;
  time_slots: Array<{ period_number: number; raw_label: string }>;
}

interface Feedback {
  kind: "success" | "error";
  text: string;
}

function StudentSchedule({ document, student }: { document: ResourceRoomProjectDocumentV2; student: Student }): React.JSX.Element {
  const slots = [...document.entities.time_slots].sort((a, b) => a.sort_order - b.sort_order);
  const courseById = new Map(document.entities.original_courses.map((item) => [item.id, item]));
  const lessonByCell = new Map(
    document.entities.student_original_lessons
      .filter((item) => item.student_id === student.id)
      .map((item) => [`${item.weekday}:${item.time_slot_id}`, item])
  );
  return (
    <div className="resource-table-scroll">
      <table className="data-table">
        <thead><tr><th>节次</th>{[1, 2, 3, 4, 5].map((day) => <th key={day}>星期{"一二三四五"[day - 1]}</th>)}</tr></thead>
        <tbody>
          {slots.map((slot) => (
            <tr key={slot.id}>
              <th>{slot.raw_label}</th>
              {[1, 2, 3, 4, 5].map((day) => {
                const lesson = lessonByCell.get(`${day}:${slot.id}`);
                return <td key={day}>{lesson ? courseById.get(lesson.original_course_id)?.name : "—"}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function StudentTimetablePage({
  projectId,
  initialTask = "ORIGINAL"
}: {
  projectId: string;
  initialTask?: SchoolDataTask;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [workbook, setWorkbook] = useState<StudentTimetableWorkbook | null>(null);
  const [fileName, setFileName] = useState("");
  const [morningLast, setMorningLast] = useState<number | "">("");
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [batchGrade, setBatchGrade] = useState("");
  const [batchClass, setBatchClass] = useState("");
  const [studentQuery, setStudentQuery] = useState("");
  const [gradeFilter, setGradeFilter] = useState("ALL");
  const [classFilter, setClassFilter] = useState("ALL");
  const [task, setTask] = useState<SchoolDataTask>(initialTask);
  const [scheduleStudentId, setScheduleStudentId] = useState<string | null>(null);
  const [editingStudentId, setEditingStudentId] = useState<string | null>(null);
  const documentQuery = useQuery({
    queryKey: ["resource-room-document", projectId],
    queryFn: async () => (await getProjectRepository()).readDocument(projectId)
  });
  const refresh = async () => {
    await queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes(projectId) });
  };
  const previewMutation = useMutation({
    mutationFn: async () => {
      if (!workbook) throw new Error("请先选择 Excel 文件");
      if (!documentQuery.data?.morning_last_time_slot_id && morningLast === "") {
        throw new Error("首次导入请先选择上午最后一节");
      }
      return (await getProjectRepository()).execute<ImportPreview>(projectId, "IMPORT_PREVIEW", {
        workbook,
        morning_last_period_number: morningLast === "" ? null : morningLast
      });
    },
    onSuccess: (result) => { setPreview(result); setFeedback(null); },
    onError: (error: unknown) => setFeedback({ kind: "error", text: error instanceof Error ? error.message : "预览失败" })
  });
  const commitMutation = useMutation({
    mutationFn: async () => {
      if (!workbook || !preview?.valid) throw new Error("请先完成有效预览");
      return (await getProjectRepository()).execute<ImportPreview>(projectId, "IMPORT_COMMIT", {
        workbook,
        morning_last_period_number: morningLast === "" ? null : morningLast
      });
    },
    onSuccess: async () => {
      setFeedback({ kind: "success", text: "原班课表已完整导入。接下来请补全年级班级，并设置课程性质。" });
      setPreview(null);
      setWorkbook(null);
      setFileName("");
      await refresh();
    },
    onError: (error: unknown) => setFeedback({ kind: "error", text: error instanceof Error ? error.message : "导入失败" })
  });
  const commandMutation = useMutation({
    mutationFn: async ({ type, payload }: { type: string; payload: Record<string, unknown> }) =>
      (await getProjectRepository()).execute(projectId, type, payload),
    onSuccess: async () => {
      setFeedback({ kind: "success", text: "学生资料已保存" });
      setEditingStudentId(null);
      await refresh();
    },
    onError: (error: unknown) => setFeedback({ kind: "error", text: error instanceof Error ? error.message : "保存失败" })
  });

  if (documentQuery.isLoading) return <main className="page-shell"><p className="state-message">正在读取学生课表…</p></main>;
  if (!documentQuery.data) return <main className="page-shell"><p className="error-message">项目无法读取。</p></main>;
  const document = documentQuery.data;
  const scheduleStudent = document.entities.students.find((student) => student.id === scheduleStudentId);
  const editingStudent = document.entities.students.find((student) => student.id === editingStudentId);
  const options = workbook ? periodOptions(workbook) : [];
  const hasImported = document.entities.students.length > 0;
  const gradeOptions = Array.from(new Set(document.entities.students.flatMap((student) => student.grade_label ? [student.grade_label] : []))).sort();
  const classOptions = Array.from(new Set(document.entities.students.flatMap((student) => student.class_name ? [student.class_name] : []))).sort();
  const normalizedStudentQuery = studentQuery.trim().toLocaleLowerCase();
  const filteredStudents = document.entities.students.filter((student) =>
    (!normalizedStudentQuery || `${student.display_name}${student.grade_label ?? ""}${student.class_name ?? ""}`.toLocaleLowerCase().includes(normalizedStudentQuery)) &&
    (gradeFilter === "ALL" || student.grade_label === gradeFilter) &&
    (classFilter === "ALL" || student.class_name === classFilter)
  );
  const planCountByStudent = new Map(
    document.entities.students.map((student) => [
      student.id,
      document.entities.intervention_plans.filter((plan) => plan.student_ids.includes(student.id)).length
    ])
  );
  const demoProfileByKey = new Map<string, (typeof demoStudentProfiles)[number]>(
    demoStudentProfiles.map((profile) => [profile.importKey, profile])
  );
  const incompleteDemoStudents = document.entities.students.filter((student) => {
    const profile = demoProfileByKey.get(student.import_key);
    return Boolean(profile && (!student.grade_label || !student.class_name));
  });
  const importedSlotCount = document.entities.time_slots.filter(
    (slot) => slot.source === "IMPORTED"
  ).length;
  const incompleteStudentCount = document.entities.students.filter(
    (student) => !student.grade_label || !student.class_name
  ).length;
  const unsetCourseCount = document.entities.original_courses.filter(
    (course) => !course.nature
  ).length;
  const schoolTasks: Array<{
    value: SchoolDataTask;
    label: string;
    status: string;
    done: boolean;
  }> = [
    {
      value: "ORIGINAL",
      label: "原班课表",
      status: importedSlotCount ? `${document.entities.students.length} 名学生` : "待导入",
      done: importedSlotCount > 0
    },
    {
      value: "STUDENTS",
      label: "学生与班级",
      status: incompleteStudentCount ? `${incompleteStudentCount} 人待补全` : "已完成",
      done: document.entities.students.length > 0 && incompleteStudentCount === 0
    },
    {
      value: "TIME_SLOTS",
      label: "编辑节次",
      status: importedSlotCount ? `${document.entities.time_slots.length} 个节次` : "等待导入",
      done: importedSlotCount > 0
    },
    {
      value: "COURSE_NATURES",
      label: "课程性质",
      status: unsetCourseCount ? `${unsetCourseCount} 项待设置` : "已完成",
      done: document.entities.original_courses.length > 0 && unsetCourseCount === 0
    }
  ];

  async function chooseFile(file: File | undefined): Promise<void> {
    if (!file) return;
    try {
      const parsed = await parseStudentTimetableFile(file);
      setWorkbook(parsed);
      setFileName(file.name);
      setPreview(null);
      setFeedback(null);
      if (!document.morning_last_time_slot_id) setMorningLast("");
    } catch (error) {
      setWorkbook(null);
      setFeedback({ kind: "error", text: error instanceof Error ? error.message : "文件读取失败" });
    }
  }

  function saveStudent(event: FormEvent<HTMLFormElement>, studentId: string): void {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    commandMutation.mutate({
      type: "UPDATE_STUDENTS",
      payload: {
        updates: [{
          student_id: studentId,
          display_name: String(form.get("display_name") ?? ""),
          grade_label: String(form.get("grade_label") ?? ""),
          class_name: String(form.get("class_name") ?? "")
        }]
      }
    });
  }

  function fillDemoWorkbook(): void {
    if (hasImported) return;
    setWorkbook(buildDemoStudentTimetableWorkbook());
    setFileName("模拟学生原班课表.xlsx");
    setMorningLast(4);
    setPreview(null);
    setFeedback({
      kind: "success",
      text: "已填入 5 名跨年级、跨班学生的模拟原班课表。请继续校验并确认导入。"
    });
  }

  function completeDemoStudentProfiles(): void {
    const updates = incompleteDemoStudents.flatMap((student) => {
      const profile = demoProfileByKey.get(student.import_key);
      return profile
        ? [{
            student_id: student.id,
            grade_label: student.grade_label || profile.grade,
            class_name: student.class_name || profile.className
          }]
        : [];
    });
    if (!updates.length) return;
    commandMutation.mutate({ type: "UPDATE_STUDENTS", payload: { updates } });
  }

  return (
    <main className="page-shell page-shell-wide">
      <ResourceRoomProjectHeader
        projectId={projectId}
        eyebrow="阶段 1 · 学校基础数据"
        title="学校基础数据"
        actions={task === "ORIGINAL" ? (
          <>
            <button
              className="secondary-button demo-data-button"
              type="button"
              disabled={hasImported}
              onClick={fillDemoWorkbook}
            >
              {hasImported ? "已有课表，不再填入模拟数据" : "填入模拟课表"}
            </button>
            <button className="secondary-button" type="button" onClick={downloadStudentTimetableTemplate}>下载 Excel 模板</button>
          </>
        ) : undefined}
      />

      <section className="resource-panel resource-school-task-panel">
        <nav className="resource-school-tasks" aria-label="学校基础数据任务">
          {schoolTasks.map((item, index) => (
            <button
              className={task === item.value ? "active" : ""}
              type="button"
              key={item.value}
              onClick={() => setTask(item.value)}
            >
              <span>{index + 1}. {item.label}</span>
              <small className={item.done ? "done" : ""}>{item.status}</small>
            </button>
          ))}
        </nav>
      </section>

      {task === "ORIGINAL" ? (
      <section className="resource-panel" aria-labelledby="import-title">
        <h2 id="import-title">{hasImported ? "重新导入原班课表" : "导入原班课表"}</h2>
        <p>Excel 仅在当前浏览器中读取，不会上传。</p>
        <label className="resource-file-input">
          <strong>选择 .xlsx 文件</strong>
          <input type="file" accept=".xlsx" onChange={(event) => void chooseFile(event.target.files?.[0])} />
          <span>{fileName || "尚未选择文件"}</span>
        </label>
        {workbook && !document.morning_last_time_slot_id ? (
          <label className="field" style={{ marginTop: 14 }}>
            <span>上午最后一节</span>
            <select value={morningLast} onChange={(event) => setMorningLast(event.target.value ? Number(event.target.value) : "")}>
              <option value="">请选择</option>
              {options.slice(0, -1).map((item) => <option key={item.periodNumber} value={item.periodNumber}>{item.label}</option>)}
            </select>
            <small className="field-hint">系统会保存这条午休边界；连续课程不会跨过它。</small>
          </label>
        ) : null}
        <div className="resource-actions" style={{ marginTop: 14 }}>
          <button className="primary-button" type="button" disabled={!workbook || previewMutation.isPending} onClick={() => previewMutation.mutate()}>
            {previewMutation.isPending ? "正在校验…" : "校验并预览"}
          </button>
        </div>
        {preview ? (
          <div>
            <div className="resource-summary-grid">
              <div className="resource-summary-card"><strong>{preview.student_count}</strong><span>本次学生 Sheet</span></div>
              <div className="resource-summary-card"><strong>{preview.course_count}</strong><span>识别原班课程</span></div>
              <div className="resource-summary-card"><strong>{preview.time_slot_count}</strong><span>统一节次</span></div>
            </div>
            {preview.errors.length ? (
              <div className="resource-errors" role="alert"><strong>整份文件尚不能导入</strong><ul>{preview.errors.map((item, index) => <li key={`${item.path}-${index}`}>{item.path}：{item.message}</li>)}</ul></div>
            ) : (
              <div className="resource-success">
                校验通过：{preview.students.filter((item) => item.action === "ADD").length} 名新增，{preview.students.filter((item) => item.action === "REPLACE").length} 名更新，{preview.retained_student_count} 名本次缺少但保留。
              </div>
            )}
            <button className="primary-button" style={{ marginTop: 14 }} type="button" disabled={!preview.valid || commitMutation.isPending} onClick={() => commitMutation.mutate()}>
              {commitMutation.isPending ? "正在导入…" : "确认整份导入"}
            </button>
          </div>
        ) : null}
      </section>
      ) : null}

      {feedback ? <p className={feedback.kind === "success" ? "resource-success" : "resource-errors"} role={feedback.kind === "error" ? "alert" : "status"}>{feedback.text}</p> : null}

      {task === "STUDENTS" ? (
      <section className="resource-panel" aria-labelledby="students-title">
        <h2 id="students-title">学生与班级信息</h2>
        <p>显示姓名可以重复，Sheet 导入识别名保持不变。年级和班级补齐后才能加入干预计划。</p>
        <div className="resource-actions demo-step-actions">
          <button
            className="secondary-button demo-data-button"
            type="button"
            disabled={!incompleteDemoStudents.length || commandMutation.isPending}
            onClick={completeDemoStudentProfiles}
          >
            {incompleteDemoStudents.length
              ? `补全模拟学生年级班级（${incompleteDemoStudents.length} 人）`
              : "模拟学生年级班级已补全"}
          </button>
        </div>
        {document.entities.students.length ? (
          <>
            <div className="resource-batch-bar" aria-label="学生筛选">
              <label className="field"><span>搜索学生</span><input value={studentQuery} onChange={(event) => setStudentQuery(event.target.value)} placeholder="姓名、年级或班级" /></label>
              <label className="field"><span>年级筛选</span><select value={gradeFilter} onChange={(event) => setGradeFilter(event.target.value)}><option value="ALL">全部年级</option>{gradeOptions.map((grade) => <option key={grade} value={grade}>{grade}</option>)}</select></label>
              <label className="field"><span>班级筛选</span><select value={classFilter} onChange={(event) => setClassFilter(event.target.value)}><option value="ALL">全部班级</option>{classOptions.map((className) => <option key={className} value={className}>{className}</option>)}</select></label>
              <span>显示 {filteredStudents.length} / {document.entities.students.length} 人</span>
            </div>
            <div className="resource-batch-bar">
              <span>已选 {selected.length} 人</span>
              <label className="field"><span>批量年级</span><input value={batchGrade} onChange={(event) => setBatchGrade(event.target.value)} placeholder="如：三年级" /></label>
              <label className="field"><span>批量班级</span><input value={batchClass} onChange={(event) => setBatchClass(event.target.value)} placeholder="如：2班" /></label>
              <button className="secondary-button" type="button" disabled={commandMutation.isPending || !selected.length || !batchGrade || !batchClass} onClick={() => commandMutation.mutate({ type: "UPDATE_STUDENTS", payload: { updates: selected.map((studentId) => ({ student_id: studentId, grade_label: batchGrade, class_name: batchClass })) } })}>批量保存</button>
            </div>
            <div className="resource-table-scroll">
              <table className="data-table">
                <thead><tr><th><span className="visually-hidden">选择</span></th><th>学生</th><th>导入识别名</th><th>年级班级</th><th>参与计划</th><th>原班课表</th><th>操作</th></tr></thead>
                <tbody>
                  {filteredStudents.map((student) => (
                    <tr key={student.id}>
                      <td><input aria-label={`选择 ${student.display_name}`} type="checkbox" checked={selected.includes(student.id)} onChange={(event) => setSelected(event.target.checked ? [...selected, student.id] : selected.filter((id) => id !== student.id))} /></td>
                      <td>{student.display_name}</td>
                      <td>{student.import_key}</td>
                      <td>{student.grade_label && student.class_name ? `${student.grade_label}${student.class_name}` : <span className="resource-status pending">待补充</span>}</td>
                      <td>{planCountByStudent.get(student.id) ?? 0} 项</td>
                      <td><button className="text-button" type="button" onClick={() => setScheduleStudentId(student.id)}>查看原班课表</button></td>
                      <td>
                        <button className="text-button" type="button" onClick={() => setEditingStudentId(student.id)}>编辑资料</button>
                      </td>
                    </tr>
                  ))}
                  {!filteredStudents.length ? <tr><td colSpan={7}>当前筛选下没有学生。</td></tr> : null}
                </tbody>
              </table>
            </div>
          </>
        ) : <div className="empty-card compact"><h2>尚未导入学生</h2><p>请先下载模板并选择填写好的 .xlsx 文件。</p></div>}
      </section>
      ) : null}

      {task === "TIME_SLOTS" ? (
        <section className="resource-panel">
          <SupplementaryTimeSlotEditor projectId={projectId} document={document} />
        </section>
      ) : null}

      {task === "COURSE_NATURES" ? (
        <CourseNaturePage projectId={projectId} embedded />
      ) : null}

      {scheduleStudent ? (
        <ResourceRoomDialog
          title={`${scheduleStudent.display_name}的原班课表`}
          description={`${scheduleStudent.grade_label ?? "年级待补充"}${scheduleStudent.class_name ?? ""} · 只读查看`}
          wide
          onClose={() => setScheduleStudentId(null)}
        >
          <StudentSchedule document={document} student={scheduleStudent} />
          <div className="modal-actions">
            <button className="secondary-button" type="button" onClick={() => setScheduleStudentId(null)}>关闭</button>
          </div>
        </ResourceRoomDialog>
      ) : null}

      {editingStudent ? (
        <ResourceRoomDialog
          title={`编辑${editingStudent.display_name}的资料`}
          description={`导入识别名“${editingStudent.import_key}”保持不变。`}
          busy={commandMutation.isPending}
          onClose={() => setEditingStudentId(null)}
        >
          <form className="resource-dialog-form" onSubmit={(event) => saveStudent(event, editingStudent.id)}>
            <label className="field"><span>显示姓名</span><input name="display_name" defaultValue={editingStudent.display_name} /></label>
            <label className="field"><span>年级</span><input name="grade_label" defaultValue={editingStudent.grade_label ?? ""} /></label>
            <label className="field"><span>班级</span><input name="class_name" defaultValue={editingStudent.class_name ?? ""} /></label>
            <div className="modal-actions resource-dialog-actions">
              <button className="text-button danger" type="button" disabled={commandMutation.isPending} onClick={() => {
                if (window.confirm(`确认删除学生“${editingStudent.display_name}”？`)) {
                  commandMutation.mutate({ type: "DELETE_STUDENT", payload: { student_id: editingStudent.id } });
                }
              }}>删除学生</button>
              <span className="resource-dialog-action-spacer" />
              <button className="secondary-button" type="button" disabled={commandMutation.isPending} onClick={() => setEditingStudentId(null)}>取消</button>
              <button className="primary-button" type="submit" disabled={commandMutation.isPending}>{commandMutation.isPending ? "正在保存…" : "保存"}</button>
            </div>
          </form>
        </ResourceRoomDialog>
      ) : null}
    </main>
  );
}
